import { EnvironmentObject } from '../data/common/EnvironmentObject';
import { RotatedBoundingBox } from '../data/common/RotatedBoundingBox';
import { LayerBitMask } from './layers/Layer';
import { PlaneView } from './views/PlaneView';

/**
 * The region of patient space a change touched, used to cull views whose viewport
 * cannot show it.\
 * An {@link EnvironmentObject} is accepted as a convenience for the (common) case where
 * the caller has the changed object at hand rather than its {@link RotatedBoundingBox}.
 */
export type InvalidationVolume = EnvironmentObject | RotatedBoundingBox;

/**
 * A "these layers changed, in this region" value.\
 * Used where an invalidation has to be carried across a boundary before it is applied,
 * e.g. {@link EventHandlerResult} handing a tool's repaint request back to
 * {@link ToolManager}.
 */
export interface ViewInvalidation {
    /**
     * Bitflag controlling the {@link Layer Layers} this invalidation affects.
     */
    readonly layers: LayerBitMask;

    /**
     * Region the change touched. If omitted, every view is repainted.
     */
    readonly volume?: InvalidationVolume;
}

/**
 * The narrow seam through which anything that mutates visualised state asks for a
 * repaint. {@link ViewManager} is the implementation; holding a reference to this
 * interface instead of reaching for the {@link ViewManager} singleton is what keeps
 * the dependency direction right.
 */
export interface ViewInvalidator {
    /**
     * Ask for the {@link Layer Layers} selected by {@link layers} to be repainted in
     * every view whose viewport can show {@link volume}.
     *
     * The repaint is not necessarily synchronous: the implementation may coalesce a
     * burst of invalidations into one repaint per view (see
     * {@link ViewManager.invalidate}, which defers by at most one animation frame).
     * Callers that must not proceed until the repaint has been issued have to say so
     * explicitly — see {@link ViewManager.flushInvalidations}.
     * @param layers Bitflag of the {@link Layer Layers} that changed.
     * @param volume Region the change touched. If omitted, all views are repainted.
     */
    invalidate(layers: LayerBitMask, volume?: InvalidationVolume): void;
}

/**
 * The host an {@link Environment} is constructed with: it both dispatches the
 * {@link Environment}'s DOM events and takes its repaint requests.
 */
export type ViewInvalidatorEventTarget = EventTarget & ViewInvalidator;

/**
 * A {@link ViewInvalidator} for a PanZoom bound to no environment. Nothing
 * resolves a view through it, so its repaint request can never be reached;
 * use it in place of a real invalidator wherever a PanZoom is constructed
 * only to satisfy a config shape.
 */
export const NO_OP_VIEW_INVALIDATOR: ViewInvalidator = {
    invalidate: () => {},
};

/**
 * Resolve an {@link InvalidationVolume} to the {@link RotatedBoundingBox} used for
 * culling. An {@link EnvironmentObject} without usable bounds resolves to undefined,
 * i.e. "no culling, update every view".
 */
export function resolveInvalidationVolume(
    volume?: InvalidationVolume
): RotatedBoundingBox | undefined {
    if (volume instanceof EnvironmentObject && volume.boundingBox.bounds) {
        return volume.boundingBox;
    }
    if (volume instanceof RotatedBoundingBox) {
        return volume;
    }
    return undefined;
}

/**
 * Whether {@link view} has to be repainted for a change inside {@link volume}.\
 * True if the view's viewport covers even a part of the volume — and, deliberately,
 * also true whenever the volume cannot be trusted, so that a malformed bounding box
 * errs towards repainting rather than towards a stale view.
 * @param volume The resolved culling volume, or undefined for "no volume given".
 * @param view The {@link PlaneView} that might be repainted.
 */
export function shouldUpdateView(
    volume: RotatedBoundingBox | undefined,
    view: PlaneView
): boolean {
    if (!volume) {
        return true; // If no volume is defined, update all views
    }

    // if the bounding box is not well constructed we should update the view (not all code can handle inverted orientations)
    if (
        !volume.bounds ||
        !volume.bounds.min ||
        !volume.bounds.max ||
        volume.bounds.min[0] > volume.bounds.max[0] ||
        volume.bounds.min[1] > volume.bounds.max[1] ||
        volume.bounds.min[2] > volume.bounds.max[2]
    ) {
        return true;
    }

    return volume.intersectsProjection(view.projectionViewMatrix);
}
